import { Controller } from "@hotwired/stimulus"

// Full-screen, swipeable photo viewer. Images sharing data-lightbox-group become
// one gallery. The viewer keeps reveal rules in the templates: it can only show
// image URLs already present in the page.
export default class extends Controller {
  disconnect() {
    if (!this.overlay) return
    this._signalOverlay(false)
    document.removeEventListener("keydown", this.escapeHandler)
    window.removeEventListener("resize", this.resizeHandler)
    document.body.style.overflow = this.previousBodyOverflow
    this.overlay.remove()
    this.overlay = null
  }

  open(event) {
    const clicked = event.currentTarget
    this.trigger = clicked
    const group = clicked.dataset.lightboxGroup
    const images = group
      ? Array.from(document.querySelectorAll(`[data-lightbox-group="${CSS.escape(group)}"]`))
      : [clicked]

    this.items = images
      .map((image, index) => ({
        src: image.src || image.dataset.src,
        label: image.alt || image.dataset.lightboxLabel || `Photo ${index + 1}`,
        meta: image.dataset.lightboxMeta || ""
      }))
      .filter((item) => item.src)
    this.index = Math.max(0, images.indexOf(clicked))
    if (this.items.length === 0) return

    this._build()
    this._show(this.index, false)
  }

  _build() {
    const overlay = document.createElement("div")
    overlay.setAttribute("role", "dialog")
    overlay.setAttribute("aria-modal", "true")
    overlay.setAttribute("aria-label", "Match photos")
    Object.assign(overlay.style, {
      position: "fixed",
      inset: "0",
      zIndex: "99999",
      overflow: "hidden",
      background: "#050505",
      color: "#fff",
      touchAction: "none",
      opacity: "0",
      transition: "opacity 180ms ease, transform 220ms ease",
      fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif"
    })
    // The iOS 26 Liquid Glass tab bar overlays the web view instead of reducing
    // its CSS viewport. env(safe-area-inset-bottom) only covers the device edge,
    // so keep viewer metadata above the native chrome as well.
    const nativeBottomChrome = window.webkit?.messageHandlers?.nativeOverlay ? "72px" : "0px"
    overlay.style.setProperty("--photo-viewer-native-bottom", nativeBottomChrome)

    const viewport = document.createElement("div")
    Object.assign(viewport.style, {
      position: "absolute",
      inset: "0",
      overflow: "hidden",
      willChange: "transform"
    })

    const track = document.createElement("div")
    Object.assign(track.style, {
      width: "100%",
      height: "100%",
      display: "flex",
      willChange: "transform",
      transition: "transform 300ms cubic-bezier(.22,.76,.25,1)"
    })

    this.photos = this.items.map((item) => {
      const slide = document.createElement("figure")
      Object.assign(slide.style, {
        flex: "0 0 100%",
        width: "100%",
        height: "100%",
        position: "relative",
        margin: "0",
        overflow: "hidden"
      })

      const photo = document.createElement("img")
      photo.alt = item.label
      photo.draggable = false
      Object.assign(photo.style, {
        width: "100%",
        height: "100%",
        position: "absolute",
        top: "0",
        left: "0",
        display: "block",
        objectFit: "contain",
        objectPosition: "center",
        transformOrigin: "center center",
        willChange: "transform",
        pointerEvents: "none",
        userSelect: "none",
        WebkitUserSelect: "none",
        transition: "transform 220ms ease"
      })

      photo.addEventListener("load", () => {
        if (this.photos?.[this.index] === photo) this._positionFooter()
      })
      photo.src = item.src

      slide.appendChild(photo)
      track.appendChild(slide)
      return photo
    })

    viewport.appendChild(track)
    overlay.appendChild(viewport)

    // Keep the viewer chrome-free while retaining an explicit close action for
    // assistive technology. Touch users swipe down; keyboard users press Escape.
    const close = document.createElement("button")
    close.type = "button"
    close.setAttribute("aria-label", "Close photo viewer")
    close.textContent = "Close photo viewer"
    Object.assign(close.style, {
      position: "absolute",
      width: "1px",
      height: "1px",
      padding: "0",
      margin: "-1px",
      overflow: "hidden",
      clip: "rect(0, 0, 0, 0)",
      whiteSpace: "nowrap",
      border: "0",
      zIndex: "2"
    })
    overlay.appendChild(close)

    const footer = document.createElement("div")
    Object.assign(footer.style, {
      position: "absolute",
      zIndex: "2",
      inset: "0 0 auto",
      display: "grid",
      gridTemplateColumns: "1fr auto 1fr",
      alignItems: "center",
      gap: "12px",
      padding: "12px 18px 18px",
      boxSizing: "border-box",
      background: "#050505",
      pointerEvents: "none"
    })

    const caption = document.createElement("div")
    const label = document.createElement("strong")
    const meta = document.createElement("span")
    Object.assign(label.style, { display: "block", fontSize: "14px" })
    Object.assign(meta.style, { display: "block", marginTop: "3px", color: "rgba(255,255,255,.72)", fontSize: "11px" })
    caption.appendChild(label)
    caption.appendChild(meta)

    const dots = document.createElement("div")
    Object.assign(dots.style, {
      display: "flex",
      alignItems: "center",
      gap: "7px",
      paddingBottom: "4px",
      pointerEvents: "auto"
    })
    this.dots = this.items.map((item, index) => {
      const dot = document.createElement("button")
      dot.type = "button"
      dot.setAttribute("aria-label", `View ${item.label}`)
      Object.assign(dot.style, {
        width: "7px",
        height: "7px",
        padding: "0",
        border: "0",
        borderRadius: "50%",
        background: "rgba(255,255,255,.42)",
        cursor: "pointer",
        transition: "background 180ms ease, transform 180ms ease"
      })
      dot.addEventListener("click", () => this._show(index))
      dots.appendChild(dot)
      return dot
    })
    if (this.items.length === 1) dots.style.visibility = "hidden"

    const counter = document.createElement("span")
    Object.assign(counter.style, {
      justifySelf: "end",
      paddingBottom: "2px",
      color: "rgba(255,255,255,.7)",
      fontSize: "11px",
      fontVariantNumeric: "tabular-nums"
    })
    if (this.items.length === 1) counter.style.visibility = "hidden"

    footer.appendChild(caption)
    footer.appendChild(dots)
    footer.appendChild(counter)
    overlay.appendChild(footer)
    // Mount outside the scrolling body so this fixed portal is anchored to the
    // screen itself, not to the feed's current scroll position in WKWebView.
    document.documentElement.appendChild(overlay)

    this.overlay = overlay
    this.viewport = viewport
    this.track = track
    this.label = label
    this.meta = meta
    this.counter = counter
    this.footer = footer
    this.scale = 1
    this.tx = 0
    this.ty = 0
    this.lastTapAt = 0
    this.previousBodyOverflow = document.body.style.overflow
    this.resizeHandler = () => this._positionFooter()
    window.addEventListener("resize", this.resizeHandler)
    document.body.style.overflow = "hidden"
    this.escapeHandler = (event) => {
      if (event.key === "Escape") this._dismiss()
    }
    document.addEventListener("keydown", this.escapeHandler)

    requestAnimationFrame(() => { overlay.style.opacity = "1" })
    close.addEventListener("click", () => this._dismiss())
    close.focus({ preventScroll: true })
    this._wireGestures()
    this._signalOverlay(true)
  }

  _show(index, animate = true) {
    this.index = Math.max(0, Math.min(this.items.length - 1, index))
    this.scale = 1
    this.tx = 0
    this.ty = 0
    this.photos.forEach((photo) => { photo.style.transform = "" })
    this.track.style.transition = animate ? "transform 300ms cubic-bezier(.22,.76,.25,1)" : "none"
    this.track.style.transform = `translate3d(${-this.index * 100}%, 0, 0)`
    this.viewport.style.transform = ""
    this.overlay.style.opacity = "1"

    this.label.textContent = this.items[this.index].label
    this.meta.textContent = this.items[this.index].meta
    this.meta.style.display = this.items[this.index].meta ? "block" : "none"
    this.counter.textContent = `${this.index + 1} / ${this.items.length}`
    this.dots.forEach((dot, dotIndex) => {
      const active = dotIndex === this.index
      dot.style.background = active ? "#fff" : "rgba(255,255,255,.42)"
      dot.style.transform = active ? "scale(1.15)" : ""
      dot.setAttribute("aria-current", active ? "true" : "false")
    })
    this._positionFooter()
  }

  _positionFooter() {
    if (!this.overlay || !this.footer) return

    const photo = this.photos[this.index]
    if (!photo?.naturalWidth || !photo?.naturalHeight) return

    const nativeViewer = Boolean(window.webkit?.messageHandlers?.nativeOverlay)
    const desktopViewer = !nativeViewer && this.overlay.clientWidth >= 900
    const mediaWidth = desktopViewer
      ? Math.min(1100, this.overlay.clientWidth - 80)
      : this.overlay.clientWidth
    const mediaLeft = (this.overlay.clientWidth - mediaWidth) / 2
    const fullWidthHeight = mediaWidth * (photo.naturalHeight / photo.naturalWidth)
    const bottomClearance = nativeViewer ? 90 : 20
    const availableHeight = Math.max(0, this.overlay.clientHeight - bottomClearance)
    const footerHeight = this.footer.offsetHeight
    const photoHeight = Math.min(fullWidthHeight, Math.max(0, availableHeight - footerHeight))
    const contentHeight = photoHeight + footerHeight
    const contentTop = Math.max(0, (availableHeight - contentHeight) / 2)

    photo.style.left = `${mediaLeft}px`
    photo.style.width = `${mediaWidth}px`
    photo.style.top = `${contentTop}px`
    photo.style.height = `${photoHeight}px`
    this.footer.style.left = `${mediaLeft}px`
    this.footer.style.right = "auto"
    this.footer.style.width = `${mediaWidth}px`
    this.footer.style.top = `${contentTop + photoHeight}px`
  }

  _applyPhotoTransform() {
    this.photos[this.index].style.transform = `translate3d(${this.tx}px, ${this.ty}px, 0) scale(${this.scale})`
  }

  _toggleZoom() {
    if (this.scale > 1) {
      this.scale = 1
      this.tx = 0
      this.ty = 0
    } else {
      this.scale = 2.25
    }
    this._applyPhotoTransform()
  }

  _dismiss() {
    if (!this.overlay) return
    this._signalOverlay(false)
    document.removeEventListener("keydown", this.escapeHandler)
    window.removeEventListener("resize", this.resizeHandler)
    document.body.style.overflow = this.previousBodyOverflow
    this.overlay.style.opacity = "0"
    setTimeout(() => {
      this.overlay?.remove()
      this.overlay = null
      this.trigger?.focus({ preventScroll: true })
    }, 220)
  }

  _signalOverlay(open) {
    window.webkit?.messageHandlers?.nativeOverlay?.postMessage(open)
  }

  _wireGestures() {
    const overlay = this.overlay
    let startX = 0
    let startY = 0
    let currentX = 0
    let currentY = 0
    let moved = false
    let pinchDistance = 0
    let pinchScale = 1
    let panX = 0
    let panY = 0
    const distance = (touches) => Math.hypot(
      touches[0].clientX - touches[1].clientX,
      touches[0].clientY - touches[1].clientY
    )

    overlay.addEventListener("touchstart", (event) => {
      moved = false
      if (event.touches.length === 2) {
        pinchDistance = distance(event.touches)
        pinchScale = this.scale
        this.photos[this.index].style.transition = "none"
        return
      }

      startX = currentX = event.touches[0].clientX
      startY = currentY = event.touches[0].clientY
      panX = this.tx
      panY = this.ty
      this.track.style.transition = "none"
      this.photos[this.index].style.transition = "none"
    }, { passive: true })

    overlay.addEventListener("touchmove", (event) => {
      moved = true
      if (event.touches.length === 2) {
        this.scale = Math.min(Math.max(pinchScale * (distance(event.touches) / pinchDistance), 1), 5)
        this._applyPhotoTransform()
        return
      }

      currentX = event.touches[0].clientX
      currentY = event.touches[0].clientY
      const dx = currentX - startX
      const dy = currentY - startY

      if (this.scale > 1) {
        this.tx = panX + dx
        this.ty = panY + dy
        this._applyPhotoTransform()
      } else if (Math.abs(dx) >= Math.abs(dy)) {
        const atStart = this.index === 0 && dx > 0
        const atEnd = this.index === this.items.length - 1 && dx < 0
        const drag = atStart || atEnd ? dx * .3 : dx
        this.track.style.transform = `translate3d(${(-this.index * overlay.clientWidth) + drag}px, 0, 0)`
        this.viewport.style.transform = ""
        overlay.style.opacity = "1"
      } else if (dy > 0) {
        this.viewport.style.transform = `translate3d(0, ${dy * .55}px, 0) scale(${Math.max(.94, 1 - dy / 1800)})`
        overlay.style.opacity = String(Math.max(.45, 1 - dy / 360))
      }
    }, { passive: true })

    const finish = () => {
      this.photos[this.index].style.transition = "transform 220ms ease"
      if (this.scale > 1) {
        if (!moved) {
          const now = Date.now()
          if (now - this.lastTapAt < 300) {
            this._toggleZoom()
            this.lastTapAt = 0
          } else {
            this.lastTapAt = now
          }
        }
        return
      }

      const dx = currentX - startX
      const dy = currentY - startY
      if (moved && dy > 90 && Math.abs(dy) > Math.abs(dx)) {
        this._dismiss()
        return
      }
      if (moved && Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy)) {
        this._show(this.index + (dx < 0 ? 1 : -1))
        return
      }

      this._show(this.index)
      if (!moved) {
        const now = Date.now()
        if (now - this.lastTapAt < 300) {
          this._toggleZoom()
          this.lastTapAt = 0
        } else {
          this.lastTapAt = now
        }
      }
    }

    overlay.addEventListener("touchend", finish, { passive: true })
    overlay.addEventListener("touchcancel", () => this._show(this.index), { passive: true })
    overlay.addEventListener("dblclick", (event) => {
      if (event.target.closest("button")) return
      this._toggleZoom()
    })
  }
}
